import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';
import classNames from 'clsx';
import { CloseRounded, SearchRounded } from '@mui/icons-material';

import { QUERY_SEARCH_KEY } from '../../constants/queryParams';
import { SEARCH_PATH } from '../../constants/routePath';

import { parseQueryObject } from '../../../Search/helpers/query';
import * as searchAutocompleteSelectors from '../../../Search/redux/selectors/autocomplete';
import { autocompleteRequestAction } from '../../../Search/redux/slices/autocomplete';
import {
  clearSearchResultAction,
  searchRequestAction,
  setSearchQueryAction,
} from '../../../Search/redux/slices/search';
import { searchAiRequestAction } from '../../../Search/redux/slices/searchAi';
import { getPathNameByParams, processHistoryObject } from '../../helpers/history';
import * as commonSelectors from '../../redux/selectors/common';

import { Autocomplete, Button, CircularProgress, InputAdornment, TextField } from '@/mui/components';

import styles from './SearchPanel.module.scss';

function SearchPanel({ value = '' }) {
  const inputRef = useRef();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const search = useSelector(commonSelectors.searchSelector);
  const genAI = useSelector(commonSelectors.genAISelector);
  const autocompleteList = useSelector(searchAutocompleteSelectors.autocompleteList);
  const autocompleteListLoaded = useSelector(searchAutocompleteSelectors.autocompleteListLoaded);
  const searchPlaceholder = useSelector(commonSelectors.searchPlaceholderSelector);
  const options = autocompleteList.map((item) => ({
    label: item,
  }));

  const onSubmit = (rawValue) => {
    inputRef.current.blur();

    const trimmedValue = rawValue.trim();

    if (!trimmedValue) {
      return;
    }

    const searchQuery = parseQueryObject(location.search);

    const query = {
      ...parseQueryObject(location.search),
      [QUERY_SEARCH_KEY]: trimmedValue,
    };

    if (searchQuery[QUERY_SEARCH_KEY] !== trimmedValue) {
      dispatch(setSearchQueryAction({}));
      navigate(
        processHistoryObject({
          pathname: getPathNameByParams(SEARCH_PATH),
          search: query,
        }),
      );
    } else {
      dispatch(clearSearchResultAction());
      dispatch(
        searchRequestAction({
          query,
          isAddRequest: false,
        }),
      );
      if (genAI) {
        dispatch(searchAiRequestAction({ query }));
      }
    }
  };

  const [inputValue, setInputValue] = useState(value);
  const [preventSubmit, setSubmitState] = useState(false);

  const onKeyUpFn = (event) => {
    setTimeout(() => {
      if (event.key === 'Enter' && !preventSubmit) {
        onSubmit(inputValue);
      }

      setSubmitState(false);
    }, 0);
  };

  useEffect(() => {
    const trimmedValue = inputValue.trim();

    if (trimmedValue) {
      dispatch(autocompleteRequestAction(trimmedValue));
    }
  }, [inputValue]);

  const shape = 'rounded';
  const size = 'xLarge';
  const placeholder = searchPlaceholder;

  const adaptedSize = size === 'xLarge' ? 'large' : size;

  return search ? (
    <Autocomplete
      freeSolo
      size={adaptedSize}
      options={options}
      optionLabelKey="label"
      optionValueKey="label"
      value={inputValue}
      disableClearable
      // initial requirements: option from the list should not be selected at all, it is by design, do not change
      isOptionEqualToValue={() => false}
      onInputChange={(event, value$) => {
        setInputValue(value$);
      }}
      onChange={(data, value$) => {
        setSubmitState(true);
        onSubmit(value$.label);
      }}
      onKeyUp={onKeyUpFn}
      renderInput={(params) => {
        inputRef.current = params.inputProps.ref.current;

        const buttonClassName = classNames(styles.InputButton, {
          [styles.InputButton___xLarge]: size === 'xLarge',
          [styles.InputButton___square]: shape === 'square',
          [styles.InputButton___rounded]: shape === 'rounded',
          [styles.InputButton___circular]: shape === 'circular',
        });

        return (
          <TextField
            {...params}
            shape={shape}
            variant="outlined"
            size={adaptedSize}
            aria-label="search-input"
            placeholder={placeholder}
            inputProps={{
              ...params.inputProps,
              className: classNames(params.inputProps.className, styles.Input, {
                [styles.Input___xLarge]: size === 'xLarge',
              }),
            }}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <InputAdornment position="end">
                  {params.InputProps.endAdornment}
                  {autocompleteListLoaded && (
                    <Button
                      aria-label="clear button"
                      type="button"
                      shape="square"
                      variant="text"
                      size={adaptedSize}
                      disableElevation
                      color="neutral"
                      square
                      className={buttonClassName}
                      onClick={() => setInputValue('')}
                      startIcon={<CircularProgress size={adaptedSize} />}
                    />
                  )}
                  {!!inputValue && (
                    <Button
                      aria-label="clear button"
                      type="button"
                      shape="square"
                      variant="text"
                      size={adaptedSize}
                      disableElevation
                      color="neutral"
                      square
                      className={buttonClassName}
                      onClick={() => setInputValue('')}
                      startIcon={<CloseRounded />}
                    />
                  )}
                  <Button
                    aria-label="search button"
                    type="button"
                    shape="square"
                    variant="outlined"
                    disableElevation
                    size={adaptedSize}
                    color="neutral"
                    square
                    className={buttonClassName}
                    onClick={(event) => {
                      event.stopPropagation();
                      onSubmit(inputValue);
                    }}
                    startIcon={<SearchRounded />}
                  />
                </InputAdornment>
              ),
            }}
          />
        );
      }}
      aria-label="Search within video field"
    />
  ) : null;
}

SearchPanel.propTypes = {
  value: PropTypes.string,
};

export default SearchPanel;
